import type { GccDashboardData, GccStage0Result, GccTaskTag, GccValueLedgerLine } from '@shared/types'

/**
 * Step 1 tagged tasks with the S0b review's exclusions applied.
 *
 * The pipeline tags every task before the practitioner reviews the register, so
 * exclusions are applied at display time. They only take effect once the review
 * has been confirmed (`proceededAt` set). Step 1 rows carry just a task name, so
 * a row is hidden only when every register entry with that name was excluded —
 * a name shared with a kept entry stays visible.
 */
export function filterExcludedTaggedTasks(gcc: GccDashboardData): GccTaskTag[] {
  const tagged = gcc.taggedTasks?.taggedTasks ?? []
  const review = gcc.s0bReview
  if (!review?.proceededAt) return tagged

  const excluded = new Set(review.tasks.filter(t => t.excluded).map(t => t.taskName))
  for (const t of review.tasks) {
    if (!t.excluded) excluded.delete(t.taskName)
  }
  if (excluded.size === 0) return tagged
  return tagged.filter(t => !excluded.has(t.taskName))
}

type RegisterProcess = GccStage0Result['taskRegister'][number]

function registerKey(processName: string, functionScope: string, taskName: string, roleTitle: string): string {
  return JSON.stringify([processName, functionScope, taskName, roleTitle])
}

/**
 * Stage 0 task register with the S0b review's exclusions removed (WI 2369).
 *
 * Review rows mirror register rows, so a row is matched on process, function,
 * task and role rather than task name alone. Each exclusion removes one matching
 * row, so of two identical rows only the excluded one is dropped. Exclusions only
 * apply once the review has been confirmed (`proceededAt` set), and processes
 * left with no tasks are dropped. The input is not mutated.
 */
/** How many rows the confirmed review excluded per register key; null when it excluded none. */
function exclusionCounts(gcc: Pick<GccDashboardData, 's0bReview'>): Map<string, number> | null {
  const review = gcc.s0bReview
  if (!review?.proceededAt) return null
  const remaining = new Map<string, number>()
  for (const t of review.tasks) {
    if (!t.excluded) continue
    const key = registerKey(t.processName, t.functionScope, t.taskName, t.roleTitle)
    remaining.set(key, (remaining.get(key) ?? 0) + 1)
  }
  return remaining.size ? remaining : null
}

export function filterExcludedRegister(gcc: GccDashboardData): RegisterProcess[] {
  const register = gcc.taskRegister?.taskRegister ?? []
  const remaining = exclusionCounts(gcc)
  if (!remaining) return register

  return register
    .map(proc => ({
      ...proc,
      tasks: proc.tasks.filter(task => {
        const key = registerKey(proc.processName, proc.functionScope, task.name, task.roleTitle)
        const count = remaining.get(key) ?? 0
        if (count === 0) return true
        remaining.set(key, count - 1)
        return false
      }),
    }))
    .filter(proc => proc.tasks.length > 0)
}

/**
 * Step 4 value ledger lines with the S0b review's exclusions removed (WI 2882), matched
 * like the register: on capability (the Step 0 process), function, task and role, one
 * line per exclusion. Exclusions don't recalculate the design, so the ledger itself
 * still holds the excluded tasks. Returns the kept lines and the ones left out.
 */
export function splitExcludedLedger(gcc: Pick<GccDashboardData, 'valueCase' | 's0bReview'>): { kept: GccValueLedgerLine[]; excluded: GccValueLedgerLine[] } {
  const lines = gcc.valueCase?.tasks ?? []
  const remaining = exclusionCounts(gcc)
  if (!remaining) return { kept: lines, excluded: [] }
  const kept: GccValueLedgerLine[] = []
  const excluded: GccValueLedgerLine[] = []
  for (const l of lines) {
    const key = registerKey(l.capabilityName, l.functionScope, l.taskName, l.roleTitle)
    const count = remaining.get(key) ?? 0
    if (count === 0) { kept.push(l); continue }
    remaining.set(key, count - 1)
    excluded.push(l)
  }
  return { kept, excluded }
}
